import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import {
  AddToCalendar,
  Button,
  Divider,
  Overline,
  TextLink,
} from '@time-line/components/common';
import { CheckCircleFilled } from '@time-line/components/icons';
import { SubscribeWizard } from 'components/subscribe/SubscribeWizard';
import { useIsDarkTheme } from 'hooks/useIsDarkMode';
import type { HeroPromotion } from 'types/cms/HeroPromotion';

import type { AnchorBlock } from '../AnchorCtas';
import { AnchorCtas } from '../AnchorCtas';
import { HERO_COLORS } from '../constants';
import { HeroWrapper } from './HeroWrapper';

export interface PromotionHeroProps extends HeroPromotion {
  title: string;
  onButtonClick?: () => void;
  hasTerms?: boolean;
  isDoctorRecommended?: boolean;
  anchorBlock?: AnchorBlock;
}

export type ColorScheme = 'white' | 'gray';

export function PromotionHero({
  bulletPoints,
  dateRange,
  hasTerms,
  onButtonClick,
  overline,
  text,
  title,
  media,
  cta,
  isDoctorRecommended,
  isForceWhiteText = false,
  anchorBlock,
}: PromotionHeroProps) {
  const video = media?._type === 'video' ? media : undefined;
  const backgroundImage = media?._type === 'background' ? media : undefined;

  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' },
  );
  const isDarkMode = useIsDarkTheme({
    color: backgroundImage?.overlayColor?.hex,
    fallback: !!video,
  });

  const colorScheme: ColorScheme =
    (!isMobile && isForceWhiteText) || isDarkMode ? 'white' : 'gray';

  return (
    <>
      <HeroWrapper
        video={video}
        backgroundImage={backgroundImage}
        isBadgeVisible={isDoctorRecommended}
      >
        <Box textAlign={{ base: 'center', md: 'left' }}>
          {overline && (
            <Overline
              color={HERO_COLORS[colorScheme].deep}
              justifyContent={{ base: 'center', md: 'flex-start' }}
              {...overline}
              mb="3xs"
            />
          )}

          {title && (
            <Text
              textAlign={{ base: 'center', md: 'left' }}
              size="h2"
              as="h1"
              color={HERO_COLORS[colorScheme].deep}
              mb={{ base: 'sm', md: '0' }}
            >
              {title}
            </Text>
          )}

          {dateRange && text && (
            <>
              <Divider
                display={{ base: 'none', md: 'block' }}
                my="sm"
                w="full"
                color={
                  colorScheme === 'white' ? 'whiteAlpha.600' : 'blackAlpha.600'
                }
              />
              <AddToCalendar
                title={title}
                description={text}
                dateRange={dateRange}
                colorScheme={colorScheme}
              />
            </>
          )}

          {text && (
            <Text
              color={HERO_COLORS[colorScheme].soft}
              size="body2"
              mt="2xs"
              mb="sm"
              textAlign={{ base: 'center', md: 'left' }}
            >
              {text}
            </Text>
          )}

          {bulletPoints && (
            <Flex
              className="hero_bullet-points"
              flexDir="column"
              gap="4xs"
              my="sm"
            >
              {bulletPoints.map((item, index) => (
                <Flex
                  key={index}
                  flexDir="row"
                  alignItems="flex-start"
                  gap="3xs"
                  color={HERO_COLORS[colorScheme].deep}
                  textAlign="left"
                >
                  <CheckCircleFilled width="xs" height="sm" />
                  <Text size="body2">{item}</Text>
                </Flex>
              ))}
            </Flex>
          )}

          {hasTerms && onButtonClick && (
            <TextLink
              as="button"
              type="button"
              aria-haspopup="dialog"
              className="hero_terms-apply"
              textProps={{
                size: 'detail',
                color: HERO_COLORS[colorScheme].gentle,
              }}
              variant="underline"
              onClick={onButtonClick}
            >
              Terms &amp; Conditions apply
            </TextLink>
          )}

          {cta?.type === 'link' && cta.link.href && cta.link?.title && (
            <Box mt="m">
              <Button
                as="a"
                colorScheme={colorScheme}
                href={cta.link.href}
                width={{ base: '100%', sm: '260px' }}
              >
                {cta.link.title}
              </Button>
            </Box>
          )}

          {cta?.type === 'subscribe' && (
            <Box className="hero_subscribe-wizard" mt="sm" w="full">
              <SubscribeWizard
                buttonColorScheme={colorScheme}
                colorScheme={colorScheme}
                consentType={cta.consentCapture?.variant || 'email'}
                autoSubscribeMarketing={
                  cta.consentCapture?.autoSubscribeMarketing
                }
                klaviyoListId={cta.klaviyoListId}
              />
            </Box>
          )}
        </Box>
        {cta?.type === 'anchor' && (
          <AnchorCtas
            anchorBlock={anchorBlock}
            colorScheme={colorScheme}
            mt="m"
          />
        )}
      </HeroWrapper>
    </>
  );
}
